import React, { useState } from 'react';
import { Box, Button, Link, Modal, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DynamicGoogleIcon from 'components/dynamic-icon/dynamicGoogleIcon';
import { sendContactData } from 'store/newFlow1/newFlow1.services';
import { useAppSelector } from 'store/hooks';
import { authState } from 'store/auth/auth.slice';

interface ITalkToUsModalProps {
   customButton?: React.ReactNode;
}

const TalkToUsModal: React.FC<ITalkToUsModalProps> = ({ customButton }) => {
   const { t } = useTranslation();
   const location = useLocation();
   const { loggedUserData } = useAppSelector(authState);
   const initialState = {
      firstName: '',
      phone: '',
   };

   const trackModalOpening = () => {
      analytics.track('talk_to_us_modal_opened', {
         location: location.pathname,
      });
   };
   const trackSendContactData = () => {
      analytics.track('talk_to_us_modal_send_contact_data', {
         location: location.pathname,
         name: formState.firstName,
         phone: formState.phone,
      });
   };
   const trackScheduledCall = () => {
      analytics.track('talk_to_us_modal_scheduled_call_clicked', {
         location: location.pathname,
      });
   };
   const [showModal, setShowModal] = useState(false);
   const [showThanksModal, setShowThanksModal] = useState(false);
   const [formState, setFormState] = useState(initialState);

   const handleCloseModal = () => setShowModal(false);
   const handleCloseThanksModal = () => setShowThanksModal(false);

   const handleOpenModal = () => {
      trackModalOpening();
      setShowModal(true);
   };
   const handleOpenThanksModal = () => setShowThanksModal(true);
   const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormState({ ...formState, firstName: e.target.value });
   };
   const handleChangePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormState({ ...formState, phone: e.target.value });
   };

   const handleSendForm = async () => {
      const userId = loggedUserData?.id;
      const data = userId
         ? { name: formState.firstName ?? 'none', phone: formState.phone, userId }
         : { name: formState.firstName ?? 'none', phone: formState.phone };
      await sendContactData(data);
      trackSendContactData();
      handleCloseModal();
      handleOpenThanksModal();
   };

   const handleScheduleCall = () => {
      trackScheduledCall();
      window.open('https://calendly.com/ronch-levy/quick-chat-about-bono-and-donations', '_blank');
   };

   const nameInputPlaceholder = t('talkToUsModal.nameInputPlaceholder');
   const phoneInputPlaceholder = t('talkToUsModal.phoneInputPlaceholder');

   return (
      <>
         {customButton ? (
            React.cloneElement(customButton as React.ReactElement<any>, { onClick: handleOpenModal })
         ) : (
            <Button
               sx={{
                  width: '100%',
                  py: 2.5,
                  '&:hover': {
                     backgroundColor: 'transparent',
                  },
               }}
               onClick={handleOpenModal}
            >
               <Typography
                  sx={{
                     textTransform: 'none',
                     textDecoration: 'underline',
                     fontSize: '16px',
                     lineHeight: '19px',
                     fontWeight: 700,
                     color: '#666666',
                  }}
               >
                  {t('talkToUsModal.mainCTA')}
               </Typography>
            </Button>
         )}
         <Modal
            open={showModal}
            onClose={handleCloseModal}
            aria-labelledby='modal-title'
            aria-describedby='modal-description'
         >
            <Box
               sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 'calc(100% - 40px)',
                  maxWidth: '350px',
                  bgcolor: 'background.paper',
                  p: 2.5,
                  textAlign: 'center',
                  borderRadius: '10px',
                  outline: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: 2.5,
               }}
            >
               <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button sx={{ justifyContent: 'end', p: 0 }} onClick={handleCloseModal}>
                     <DynamicGoogleIcon iconName='close' />
                  </Button>
               </Box>

               <Typography
                  sx={{ textAlign: 'left', fontWeight: '700', fontSize: '24px', lineHeight: '26px', color: '#333333' }}
               >
                  {t('talkToUsModal.title')}
               </Typography>
               <Typography
                  id='modal-description'
                  sx={{
                     color: '#666666',
                     textAlign: 'start',
                     fontWeight: '600',
                     fontSize: '16px',
                     lineHeight: '19px',
                     whiteSpace: 'pre-line',
                  }}
               >
                  {t('talkToUsModal.text')}
               </Typography>

               <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 1.25 }}>
                  <TextField
                     fullWidth
                     className='CommonInput'
                     value={formState.firstName}
                     type='text'
                     onChange={handleChangeName}
                     placeholder={nameInputPlaceholder}
                  />
                  <TextField
                     fullWidth
                     required
                     className='CommonInput'
                     value={formState.phone}
                     type='text'
                     onChange={handleChangePhone}
                     placeholder={phoneInputPlaceholder}
                  />
               </Box>

               <Button variant='primaryBtn' disabled={formState.phone.length === 0} onClick={handleSendForm}>
                  <Typography>{t('talkToUsModal.button1')}</Typography>
               </Button>
               <Typography sx={{ color: '#999999', fontSize: '16px', lineHeight: '19px', fontWeight: 600 }}>
                  or
               </Typography>
               <Button variant='primaryBtn' onClick={handleScheduleCall}>
                  <Typography>{t('talkToUsModal.button2')}</Typography>
               </Button>
            </Box>
         </Modal>
         <Modal
            open={showThanksModal}
            onClose={handleCloseThanksModal}
            aria-labelledby='modal-title'
            aria-describedby='modal-description'
         >
            <Box
               sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 'calc(100% - 40px)',
                  maxWidth: '350px',
                  bgcolor: 'background.paper',
                  p: 2.5,
                  textAlign: 'center',
                  borderRadius: '10px',
                  outline: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: 2.5,
               }}
            >
               <Typography
                  sx={{ textAlign: 'left', fontWeight: '700', fontSize: '24px', lineHeight: '26px', color: '#333333' }}
               >
                  {t('thanksModal.title')}
               </Typography>
               <Typography
                  sx={{
                     color: '#666666',
                     textAlign: 'start',
                     fontWeight: '600',
                     fontSize: '16px',
                     lineHeight: '19px',
                     whiteSpace: 'pre-line',
                  }}
               >
                  {t('thanksModal.text')}
               </Typography>
               <Button variant='primaryBtn' onClick={handleCloseThanksModal}>
                  <Typography>{t('thanksModal.mainCTA')}</Typography>
               </Button>
            </Box>
         </Modal>
      </>
   );
};

export default TalkToUsModal;
